import { ResourceSignature, ResourceType } from '@/modules/types/resource';

/** Where every uploaded resource file lands after publish (see transformElearningJson). */
const SIGNED_ASSET_PREFIX = '/published-content-assets/';
const RESOURCES_FOLDER = '/pages/resources/';

/**
 * True for the URLs the API's resource signature actually covers: a same-origin
 * path under this course's published `pages/resources/` folder. External links
 * (and anything else) are outside the signed policy, so a signature on them is
 * useless and would only leak into a third party's access log.
 */
export const isSignableResourceUrl = (url: string): boolean =>
  url.startsWith(SIGNED_ASSET_PREFIX) && url.includes(RESOURCES_FOLDER);

/**
 * Appends the CloudFront signed-URL triple to every file resource link.
 *
 * Resource Library files open in a new tab. Under a cross-site embed (Breathe,
 * SCORM) the player's signed cookies are Partitioned to the LMS top-level site,
 * so the new tab sends none of them and CloudFront answers MissingKey. The
 * signature in the query string carries the grant instead (LMS2-5136).
 *
 * Signing is decided by URL shape, not by the authored `type`: the form lets an
 * author label a file as LINK or a link as PDF. Without a signature (older API)
 * the resources come back unchanged.
 */
export const signResourceUrls = (
  resources: ResourceType[] | undefined | null,
  signature?: ResourceSignature | null,
): ResourceType[] => {
  if (!resources) return [];
  if (!signature) return resources;

  // Same encoding the CloudFront signer uses for its own URLs: the values are
  // URL-safe base64 (`-`, `_`, `~`), which encodeURIComponent leaves untouched.
  const query = (
    ['Policy', 'Signature', 'Key-Pair-Id'] as (keyof ResourceSignature)[]
  )
    .map((key) => `${key}=${encodeURIComponent(signature[key])}`)
    .join('&');

  return resources.map((resource) => {
    const { url } = resource.elearningResourceMetadata;
    if (!url || !isSignableResourceUrl(url)) return resource;

    // Keep any #fragment last; the query must sit before it.
    const hashIndex = url.indexOf('#');
    const path = hashIndex === -1 ? url : url.slice(0, hashIndex);
    const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
    const separator = path.includes('?') ? '&' : '?';

    return {
      ...resource,
      elearningResourceMetadata: {
        ...resource.elearningResourceMetadata,
        url: `${path}${separator}${query}${hash}`,
      },
    };
  });
};
